import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Types
import { OrderDetailsProp } from '../types';

// Helpers
import { formatFullName } from '../helpers';

// Styles
import styles from '../order-details.upp.module.scss';
import { DescriptionList, DescriptionPair } from './description-list';

const Insurance = (props: {
  label?: string;
  provider: string;
  fullName: string;
  memberId: string;
  groupNumber: string;
}) => {
  const { t } = useTranslation();
  const { label, provider, fullName, memberId, groupNumber } = props;

  return (
    <div>
      {label ? (
        <Heading level={3} className={styles.listHeading}>
          {label}
        </Heading>
      ) : null}
      <DescriptionList>
        <DescriptionPair
          term={t('orders.details.insurance.policyholder')}
          description={fullName}
        />
        <DescriptionPair
          term={t('orders.details.insurance.provider')}
          description={provider}
        />
        <DescriptionPair
          term={t('orders.details.insurance.memberID')}
          description={memberId}
        />
        <DescriptionPair
          term={t('orders.details.insurance.groupNumber')}
          description={groupNumber}
        />
      </DescriptionList>
    </div>
  );
};

type Props = {
  orderDetails: OrderDetailsProp;
};

export const PaymentInformation = ({ orderDetails }: Props) => {
  const { t } = useTranslation();
  const primaryInsuranceCard = orderDetails?.billingInfo?.primaryInsuranceCard;
  const secondaryInsuranceCard =
    orderDetails?.billingInfo?.secondaryInsuranceCard;
  const primaryInsuranceProvider =
    primaryInsuranceCard?.insuranceProvider || t('labels.na')!;
  const primaryFirstName = primaryInsuranceCard?.patientFirstName;
  const primaryLastName = primaryInsuranceCard?.patientLastName;
  const primaryFullName =
    formatFullName(primaryFirstName, primaryLastName) || t('labels.na')!;
  const primaryMemberID = primaryInsuranceCard?.memberId || t('labels.na')!;
  const primaryGroupNumber =
    primaryInsuranceCard?.groupNumber || t('labels.na')!;
  const secondaryInsuranceProvider =
    secondaryInsuranceCard?.insuranceProvider || t('labels.na')!;
  const secondaryFirstName = secondaryInsuranceCard?.patientFirstName;
  const secondaryLastName = secondaryInsuranceCard?.patientLastName;
  const secondaryFullName =
    formatFullName(secondaryFirstName, secondaryLastName) || t('labels.na')!;
  const secondaryMemberID = secondaryInsuranceCard?.memberId || t('labels.na')!;
  const secondaryGroupNumber =
    secondaryInsuranceCard?.groupNumber || t('labels.na')!;
  const creditCard = orderDetails.billingInfo?.creditCard;
  const creditCardName = creditCard?.name || t('labels.na')!;
  const billingDescription = orderDetails.billingInfo?.description;

  return (
    <section className={styles.sectionWrapper}>
      <div className={styles.paymentSectionWrapper}>
        <div className={styles.iconWrapper}>
          <FontAwesomeIcon
            icon={faMoneyCheckDollar}
            className={styles.icon}
            aria-hidden
          />
        </div>
        <div className={styles.paymentSectionContent}>
          <Heading level={2} className={styles.sectionHeading}>
            {t('orders.details.paymentInformation')}
          </Heading>
          {primaryInsuranceCard && !secondaryInsuranceCard ? (
            <Insurance
              provider={primaryInsuranceProvider}
              fullName={primaryFullName}
              memberId={primaryMemberID}
              groupNumber={primaryGroupNumber}
            />
          ) : null}
          {primaryInsuranceCard && secondaryInsuranceCard ? (
            <>
              <Insurance
                label={t('orders.details.insurance.primaryInsurance')}
                provider={primaryInsuranceProvider}
                fullName={primaryFullName}
                memberId={primaryMemberID}
                groupNumber={primaryGroupNumber}
              />
              <Insurance
                label={t('orders.details.insurance.secondaryInsurance')}
                provider={secondaryInsuranceProvider}
                fullName={secondaryFullName}
                memberId={secondaryMemberID}
                groupNumber={secondaryGroupNumber}
              />
            </>
          ) : null}
          {creditCard ? (
            <div>
              <Heading level={3} className={styles.listHeading}>
                {t('orders.details.creditCard')}
              </Heading>
              <DescriptionList>
                <DescriptionPair
                  term={t('orders.details.credit.name')}
                  description={creditCardName}
                />
              </DescriptionList>
            </div>
          ) : null}
          {billingDescription ? (
            <p className={styles.description}>{billingDescription}</p>
          ) : null}
          {!primaryInsuranceCard &&
          !secondaryInsuranceCard &&
          !creditCard &&
          !billingDescription ? (
            <p className={styles.description}>
              {t('orders.details.unavailable')}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
};
